import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { fmt, fmtDate } from './constants';

// Official PCEA colours
const RED   = [204, 0, 0];      // #CC0000
const BLUE  = [0, 51, 153];     // #003399
const WHITE = [255, 255, 255];
const LGRAY = [245, 245, 245];
const DGRAY = [50, 50, 50];

// ── Load logo as base64 for PDF ────────────────────────────────
let LOGO_B64 = null;
async function getLogoBase64() {
  if (LOGO_B64) return LOGO_B64;
  try {
    const resp = await fetch('/pcea-logo.png');
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { LOGO_B64 = reader.result; resolve(LOGO_B64); };
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── PDF Header ─────────────────────────────────────────────────
async function pdfHeader(doc, title, sub) {
  const pw = doc.internal.pageSize.width;

  // Blue top banner
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, pw, 32, 'F');

  // Red accent stripe
  doc.setFillColor(...RED);
  doc.rect(0, 32, pw, 3, 'F');

  // Logo
  const logo = await getLogoBase64();
  if (logo) {
    doc.addImage(logo, 'PNG', 6, 2, 26, 26);
  }

  // Church name
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('PCEA Thingati Church', 37, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Ngecha Parish,Ngecha Presbytery · Kiambu County, Kenya', 37, 20);
  doc.text('Faith · Love · Hope', 37, 26);

  // Report title (right side)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, pw - 10, 13, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(sub, pw - 10, 20, { align: 'right' });
  doc.text(`Generated: ${new Date().toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })}`, pw - 10, 26, { align: 'right' });

  doc.setTextColor(...DGRAY);
}

// ── PDF Footer ─────────────────────────────────────────────────
function pdfFooter(doc) {
  const pages = doc.internal.getNumberOfPages();
  const pw    = doc.internal.pageSize.width;
  const ph    = doc.internal.pageSize.height;
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    // Footer line
    doc.setFillColor(...BLUE);
    doc.rect(0, ph - 12, pw, 0.5, 'F');
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text('PCEA Thingati Church Management System · Confidential', 10, ph - 6);
    doc.text(`Page ${i} of ${pages}`, pw - 10, ph - 6, { align: 'right' });
  }
}

// ── MEMBERS PDF ────────────────────────────────────────────────
export async function generateMembersPDF(members, title = 'Members Report') {
  const doc = new jsPDF();
  await pdfHeader(doc, title, `Total: ${members.length} members`);

  autoTable(doc, {
    startY: 40,
    head: [['Member No.', 'Name', 'District', 'Type', 'Phone', 'Status', 'Joined']],
    body: members.map(m => [
      m.member_number || '—',
      `${m.first_name} ${m.last_name}`,
      m.districts?.name || '—',
      m.membership_type || '—',
      m.phone || '—',
      m.status,
      fmtDate(m.joined_date),
    ]),
    headStyles:         { fillColor: BLUE, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
    bodyStyles:         { fontSize: 8 },
    alternateRowStyles: { fillColor: LGRAY },
    columnStyles:       { 0: { cellWidth: 22 } },
  });

  pdfFooter(doc);
  doc.save('pcea_thingati_members.pdf');
}

// ── FINANCIAL PDF ──────────────────────────────────────────────
export async function generateFinancialPDF(contributions, summary, period = 'Annual') {
  const doc = new jsPDF();
  await pdfHeader(doc, `Financial Report`, period);

  // Summary boxes
  let y = 42;
  const boxes = [
    ['Tithes',         summary.tithes    || 0, RED  ],
    ['Offerings',      summary.offerings  || 0, BLUE ],
    ['Special',        summary.special   || 0, [46, 125, 50]],
    ['Building Fund',  summary.building  || 0, [230, 81, 0]],
  ];
  const bw = 44;
  boxes.forEach(([label, val, color], i) => {
    const x = 10 + i * (bw + 2);
    doc.setFillColor(...color);
    doc.roundedRect(x, y, bw, 16, 2, 2, 'F');
    doc.setTextColor(...WHITE);
    doc.setFontSize(6.5); doc.text(label, x + bw / 2, y + 5.5, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text(fmt(val), x + bw / 2, y + 13, { align: 'center' });
    doc.setFont('helvetica', 'normal');
  });
  y += 22; doc.setTextColor(...DGRAY);

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Member', 'District', 'Type', 'Amount (KES)', 'Method', 'Notes']],
    body: contributions.map(c => [
      fmtDate(c.date),
      c.members ? `${c.members.first_name} ${c.members.last_name}` : '—',
      c.members?.districts?.name || '—',
      c.type,
      Number(c.amount).toLocaleString(),
      c.payment_method || 'cash',
      c.notes || '—',
    ]),
    headStyles:         { fillColor: BLUE, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    bodyStyles:         { fontSize: 7.5 },
    alternateRowStyles: { fillColor: LGRAY },
    foot:               [['', '', '', 'TOTAL', Number(summary.total || 0).toLocaleString(), '', '']],
    footStyles:         { fillColor: RED, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
  });

  pdfFooter(doc);
  doc.save(`pcea_financial_${period.replace(/\s/g, '_').toLowerCase()}.pdf`);
}

// ── ATTENDANCE PDF ─────────────────────────────────────────────
export async function generateAttendancePDF(services) {
  const doc = new jsPDF('l');
  await pdfHeader(doc, 'Attendance Report', `${services.length} services recorded`);

  autoTable(doc, {
    startY: 40,
    head: [['Date', 'Sermon Title', 'Preacher', 'Attendance', 'Holy Communion', 'Offering (KES)']],
    body: services.map(s => [
      fmtDate(s.date),
      s.sermon_title || '—',
      s.preacher     || '—',
      s.attendance_count || 0,
      s.communion_count  || 0,
      Number(s.total_offering || 0).toLocaleString(),
    ]),
    headStyles:         { fillColor: BLUE, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
    bodyStyles:         { fontSize: 8 },
    alternateRowStyles: { fillColor: LGRAY },
  });

  pdfFooter(doc);
  doc.save('pcea_attendance.pdf');
}

// ── ANNUAL REPORT PDF ──────────────────────────────────────────
export async function generateAnnualReportPDF(data, year) {
  const doc = new jsPDF();
  await pdfHeader(doc, `Annual Report ${year}`, 'Comprehensive Church Report');

  let y = 42;
  const ph = doc.internal.pageSize.height;
  const pw = doc.internal.pageSize.width;

  const section = async (title) => {
    if (y > ph - 50) { doc.addPage(); await pdfHeader(doc, `Annual Report ${year}`, 'Continued'); y = 42; }
    doc.setFillColor(...BLUE);
    doc.rect(10, y, pw - 20, 8, 'F');
    doc.setTextColor(...WHITE); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(title, 14, y + 5.5);
    doc.setTextColor(...DGRAY); doc.setFont('helvetica', 'normal');
    y += 12;
  };

  const kv = (label, value, bold = false) => {
    if (y > ph - 18) { doc.addPage(); y = 20; }
    doc.setFontSize(8.5);
    if (bold) { doc.setFont('helvetica', 'bold'); }
    doc.text(label, 14, y);
    doc.text(String(value), pw - 14, y, { align: 'right' });
    if (bold) doc.setFont('helvetica', 'normal');
    doc.setDrawColor(210); doc.line(14, y + 2, pw - 14, y + 2);
    y += 7;
  };

  await section('MEMBERSHIP SUMMARY');
  kv('Total Active Members', data.totalMembers || 0);
  kv('Full Members', data.fullMembers || 0);
  kv('Associate Members', data.associateMembers || 0);
  kv('Adherents', data.adherents || 0);
  kv('New Members This Year', data.newMembers || 0);
  kv('Confirmations This Year', data.confirmations?.length || 0);
  kv('Baptisms This Year', data.baptisms?.length || 0);
  kv('Weddings This Year', data.weddings?.length || 0);
  kv('Burials This Year', data.burials?.length || 0);
  kv('Deceased (recorded)', data.deceased || 0);
  y += 4;

  await section('FINANCIAL SUMMARY');
  kv('Tithes', fmt(data.tithes || 0));
  kv('Offerings', fmt(data.offerings || 0));
  kv('Thanksgiving Offerings', fmt(data.thanksgiving || 0));
  kv('Harvest Offerings', fmt(data.harvest || 0));
  kv('Building Fund', fmt(data.building || 0));
  kv('Special Contributions', fmt(data.special || 0));
  kv('Other Income', fmt(data.otherIncome || 0));
  kv('GRAND TOTAL INCOME', fmt(data.grandTotal || 0), true);
  y += 4;

  await section('ATTENDANCE & WORSHIP');
  kv('Sunday Services Held', data.servicesCount || 0);
  kv('Total Attendance', data.totalAttendance || 0);
  kv('Average per Service', Math.round((data.totalAttendance || 0) / Math.max(data.servicesCount || 1, 1)));
  kv('Holy Communion Services', data.communionServices || 0);
  kv('Total Communion Attendees', data.communionTotal || 0);
  y += 4;

  await section('CONTRIBUTIONS BY DISTRICT');
  Object.entries(data.byDistrict || {}).sort((a,b)=>b[1]-a[1]).forEach(([d, v]) => {
    kv(d, typeof v === 'object' ? fmt(v.total||0) : fmt(v));
  });
  y += 4;

  await section('GROUPS & MINISTRIES');
  Object.entries(data.byGroup || {}).sort((a,b)=>b[1]-a[1]).forEach(([g, c]) => {
    kv(g, `${c} members`);
  });

  pdfFooter(doc);
  doc.save(`pcea_annual_report_${year}.pdf`);
}

// ── TRACKING PDF ───────────────────────────────────────────────
export async function generateTrackingPDF(records, type, year) {
  const doc = new jsPDF();
  const titles = {
    burials: 'Burial Records', weddings: 'Wedding Records',
    confirmations: 'Confirmation Records', baptisms: 'Baptism Records',
  };
  await pdfHeader(doc, titles[type] || 'Life Events', `${year} · Total: ${records.length} records`);

  let head, body;
  if (type === 'burials') {
    head = [['Full Name', 'Date', 'District', 'Age', 'Officiant', 'Cause']];
    body = records.map(r => [r.full_name, fmtDate(r.date), r.district||'—', r.age||'—', r.officiant||'—', r.cause_of_death||'—']);
  } else if (type === 'weddings') {
    head = [['Groom', 'Bride', 'Date', 'District', 'Officiant', 'Venue']];
    body = records.map(r => [r.groom_name, r.bride_name, fmtDate(r.date), r.district||'—', r.officiant||'—', r.venue||'—']);
  } else if (type === 'confirmations') {
    head = [['Full Name', 'Date', 'District', 'Officiant', 'Notes']];
    body = records.map(r => [r.full_name, fmtDate(r.date), r.district||'—', r.officiant||'—', r.notes||'—']);
  } else {
    head = [['Full Name', 'Date', 'District', 'Date of Birth', 'Parent/Guardian', 'Officiant']];
    body = records.map(r => [r.full_name, fmtDate(r.date), r.district||'—', r.dob?fmtDate(r.dob):'—', r.parent_names||'—', r.officiant||'—']);
  }

  autoTable(doc, {
    startY: 40,
    head, body,
    headStyles:         { fillColor: BLUE, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
    bodyStyles:         { fontSize: 8 },
    alternateRowStyles: { fillColor: LGRAY },
  });

  pdfFooter(doc);
  doc.save(`pcea_${type}_${year}.pdf`);
}

// ── EXCEL EXPORTS ──────────────────────────────────────────────
export function exportMembersExcel(members) {
  const rows = members.map(m => ({
    'Member Number':     m.member_number || '',
    'First Name':        m.first_name,
    'Last Name':         m.last_name,
    'Gender':            m.gender || '',
    'Date of Birth':     fmtDate(m.dob),
    'Marital Status':    m.marital_status || '',
    'Phone':             m.phone || '',
    'Email':             m.email || '',
    'National ID':       m.id_number || '',
    'District':          m.districts?.name || '',
    'Family':            m.families?.name || '',
    'Family Role':       m.family_role || '',
    'Membership Type':   m.membership_type || '',
    'Status':            m.status,
    'Area':              m.area || '',
    'Joined Date':       fmtDate(m.joined_date),
    'Baptism Date':      fmtDate(m.baptism_date),
    'Confirmation Date': fmtDate(m.confirmation_date),
    'Groups':            (m.member_groups||[]).map(g=>g.groups?.name).filter(Boolean).join(', '),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Members');
  XLSX.writeFile(wb, 'pcea_thingati_members.xlsx');
}

export function exportFinancialsExcel(contributions, year) {
  const rows = contributions.map(c => ({
    'Date':             fmtDate(c.date),
    'Member':           c.members ? `${c.members.first_name} ${c.members.last_name}` : '',
    'Member Number':    c.members?.member_number || '',
    'District':         c.members?.districts?.name || '',
    'Type':             c.type,
    'Amount (KES)':     Number(c.amount),
    'Payment Method':   c.payment_method || 'cash',
    'Reference':        c.reference_no || '',
    'Notes':            c.notes || '',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Contributions ${year}`);
  XLSX.writeFile(wb, `pcea_financials_${year}.xlsx`);
}

export function exportAttendanceExcel(services) {
  const rows = services.map(s => ({
    'Date':             fmtDate(s.date),
    'Sermon':           s.sermon_title || '',
    'Preacher':         s.preacher || '',
    'Attendance':       s.attendance_count || 0,
    'Holy Communion':   s.communion_count  || 0,
    'Offering (KES)':   Number(s.total_offering || 0),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
  XLSX.writeFile(wb, 'pcea_attendance.xlsx');
}